import { PhotoMarker } from '../types/types';
import { loadImageAsBlobUrl, loadImage } from './downloadLayoutImage';

/**
 * Generates a layout image with only the specified marker visible.
 * Uses backend proxy to bypass CORS issues with signed GCS URLs.
 *
 * @param layoutImageUrl - URL of the layout background image
 * @param marker - The marker to display (single marker only)
 * @param width - Layout image width
 * @param height - Layout image height
 * @returns Promise<Blob> PNG blob of the layout with single marker
 */
export async function getLayoutImageWithSingleMarker(
  layoutImageUrl: string,
  marker: PhotoMarker,
  width: number,
  height: number
): Promise<Blob> {
  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  ctx.scale(scale, scale);

  // Fill white background
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, width, height);

  // Load and draw the background layout image through proxy
  const blobUrl = await loadImageAsBlobUrl(layoutImageUrl);
  const backgroundImg = await loadImage(blobUrl);
  ctx.drawImage(backgroundImg, 0, 0, width, height);
  URL.revokeObjectURL(blobUrl);

  // Draw single marker: rect + number
  const rectX = marker.x - marker.width / 2;
  const rectY = marker.y - marker.height / 2;

  // Marker rect (blue fill, black stroke - matching layout style)
  ctx.fillStyle = '#4477ff';
  ctx.strokeStyle = 'black';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.rect(rectX, rectY, marker.width, marker.height);
  ctx.fill();
  ctx.stroke();

  // Marker number text
  ctx.fillStyle = 'white';
  ctx.font = `600 ${marker.fontSize}px Inter, Helvetica, Arial, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(marker.number), marker.x, marker.y);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
        } else {
          resolve(blob);
        }
      },
      'image/png',
      1.0
    );
  });
}

/**
 * Generates a zoomed-in layout image showing ~30% of the layout centered on the marker.
 * When centering would create whitespace at edges, the crop is clamped so the layout
 * fills the view and the marker may be off-center.
 *
 * @param layoutImageUrl - URL of the layout background image
 * @param marker - The marker to center on
 * @param width - Full layout image width
 * @param height - Full layout image height
 * @returns Promise<Blob> PNG blob of the zoomed view
 */
export async function getLayoutImageZoomedOnMarker(
  layoutImageUrl: string,
  marker: PhotoMarker,
  width: number,
  height: number,
  outputSize: number = 300
): Promise<Blob> {
  const cropFraction = 0.3;
  const cropWidth = Math.max(1, width * cropFraction);
  const cropHeight = Math.max(1, height * cropFraction);

  // Center crop on marker, clamp to layout bounds to avoid whitespace
  const cropLeft = Math.max(0, Math.min(marker.x - cropWidth / 2, width - cropWidth));
  const cropTop = Math.max(0, Math.min(marker.y - cropHeight / 2, height - cropHeight));

  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = outputSize * scale;
  canvas.height = outputSize * scale;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  ctx.scale(scale, scale);

  // Fill white background
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, outputSize, outputSize);

  // Load and draw the cropped portion of the layout
  const blobUrl = await loadImageAsBlobUrl(layoutImageUrl);
  const backgroundImg = await loadImage(blobUrl);
  ctx.drawImage(
    backgroundImg,
    cropLeft,
    cropTop,
    cropWidth,
    cropHeight, // source: crop region
    0,
    0,
    outputSize,
    outputSize // dest: fixed square output
  );
  URL.revokeObjectURL(blobUrl);

  // Marker position relative to crop
  const markerRelX = marker.x - cropLeft;
  const markerRelY = marker.y - cropTop;
  const rectX = markerRelX - marker.width / 2;
  const rectY = markerRelY - marker.height / 2;

  // Marker rect (blue fill, black stroke)
  ctx.fillStyle = '#4477ff';
  ctx.strokeStyle = 'black';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.rect(rectX, rectY, marker.width, marker.height);
  ctx.fill();
  ctx.stroke();

  // Marker number text
  ctx.fillStyle = 'white';
  ctx.font = `600 ${marker.fontSize}px Inter, Helvetica, Arial, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(marker.number), markerRelX, markerRelY);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
        } else {
          resolve(blob);
        }
      },
      'image/png',
      1.0
    );
  });
}
